import api from './api-service';
import TranslationReplicator from './translation-replicator';
import stripTags from './strip-tags-service';
import SRTService from './srt-service';

const TranslationService = {

	getById(id) {
		return api.client.get(`/translations/${id}`)
			.then((res) => res.data)
			.catch((e) => {
				if( e.status === 404 ) {
					return null;
				}

				throw e;
			});
	},

	isSubtitlerTranslation(translation) {
		return translation
			&& !translation.file
			&& translation.lines
			&& (translation.status === 'submitted' || translation.status === 'complete');
	},

	isCustomTranslation(translation) {
		return !!translation?.file;
	},

	/**
	 * Returns true when the translation has reached (or passed) the submitted
	 * step in its lifecycle. Translations that are still 'incomplete' or
	 * 'for-review' are considered NOT submitted and their files/contents
	 * should not be made available for download.
	 */
	isSubmittedTranslation(translation) {
		return !!translation
			&& (translation.status === 'submitted' || translation.status === 'complete');
	},

	getTranslationMemories(id, language) {
		// Dual languages (e.g. "GER-PFR") need separate API calls
		// because translation memories are stored per individual language.
		// Results are joined with <span></span> separator to match the
		// dual-language editor rendering format.
		if(language && language.includes('-')) {
			const languages = language.split('-');
			return Promise.all(
				languages.map((lang) => api.client.get(`/translations/translate?id=${id}&language=${lang.trim()}`)
					.then(({ data }) => data))
			).then((results) => {
				const maxLength = Math.max(...results.map((r) => (Array.isArray(r) ? r.length : 0)));
				const merged = [];
				for(let i = 0; i < maxLength; i++) {
					const parts = results.map((result) => {
						if(Array.isArray(result) && i < result.length && result[i] !== null) {
							return result[i];
						}
						return null;
					});
					// If all parts are null, the line has no TM match
					if(parts.every((p) => p === null)) {
						merged.push(null);
					}
					else {
						// Join with <span></span> separator for dual-language rendering;
						// use empty string for any language that has no match
						merged.push(parts.map((p) => p || '').join('<span></span>'));
					}
				}
				return merged;
			});
		}

		return api.client.get(`/translations/translate?id=${id}&language=${language}`)
			.then(({ data }) => data);
	},

	getMachineTranslations(id, language) {
		return api.client.get(`/translations/translate?id=${id}&language=${language}&machine=true`)
			.then(({ data }) => data);
	},

	getTranslationLanguages() {
		return api.client.get(`/translations/languages`)
			.then(({ data }) => data);
	},

	newlineToDivs(value) {
		let translationText = value ? (value.includes('<div>') ? value : `<div>${value}</div>`) : '<div></div>';

		// since we prefer divs over newlines, convert them
		translationText = translationText.replace(/\r\n/g, '</div><div>');
		translationText = translationText.replace(/\r/g, '</div><div>');
		translationText = translationText.replace(/\n/g, '</div><div>');

		// remove any useless empty divs
		return translationText.replace(/<div><\/div>/g, '');
	},

	autoTranslate(subtitles, applyTranslationMemories, applyMachineTranslations) {

		const {
			translations,
			tmTranslations,
			machineTranslations,
			ovTranslations
		} = subtitles;

		return translations?.map((translation, i) => {
			// Manual (custom) edits are the top layer and are never overwritten
			// by TM/MT. Precedence: Custom > TM > MT > OV.
			if( translation.translationFrom === 'custom' ) {
				return translation;
			}

			const ovText = translation.ovText
				|| ovTranslations?.printLines?.[i]?.text
				|| ovTranslations?.lines?.[i]?.text;
			const tmTranslationText = tmTranslations[i];
			let mtTranslationText = machineTranslations[i];
			let translationFrom;
			let translationText;
			let auto;
			let machine;

			// MT applies first; TM overwrites per-line where a match exists, so
			// when both toggles are on TM takes precedence and MT backfills.
			if( applyMachineTranslations && mtTranslationText ) {
				translationText = this.newlineToDivs(mtTranslationText);
				translationFrom = 'mt';
				auto = false;
				machine = true;
			}

			if( applyTranslationMemories && tmTranslationText && stripTags(tmTranslationText).length > 0 ) {
				translationText = this.newlineToDivs(tmTranslationText);
				translationFrom = 'tm';
				auto = true;
				machine = false;
			}

			if( !translationText ) {
				// Keep the line when the source it already came from is still switched
				// on but that source has no data to offer yet. A refetch (e.g. after
				// saving) rebuilds `subtitles` with empty tmTranslations /
				// machineTranslations while the toggles stay on; falling through to OV
				// here wipes the saved translation on screen, and the next save then
				// persists that revert. Checking the line's own source keeps the
				// genuine "toggled off → back to OV" behaviour intact.
				const from = translation.translationFrom;
				const sourceStillOn = (from === 'mt' && applyMachineTranslations)
					|| (from === 'tm' && applyTranslationMemories);
				const sourceHasNoData = (from === 'mt' && !machineTranslations?.length)
					|| (from === 'tm' && !tmTranslations?.length);

				if( sourceStillOn && sourceHasNoData ) {
					return translation;
				}

				translationText = ovText;
				translationFrom = 'ov';
				auto = false;
				machine = false;
			}

			return {
				...translation,
				translationFrom,
				translationText,
				auto,
				machine
			};
		}) ?? [];
	},

	createTranslation(data) {
		return api.client.post(`/translations`, data);
	},

	updateTranslation(id, data) {
		return api.client.patch(`/translations/${id}`, data);
	},

	translationReplicator(asset, translation, type = 'srt') {
		return TranslationReplicator.translate(asset, translation, type);
	},

	stripTags(text) {
		return stripTags(text, '')
			.replace(/&nbsp;/g, '')
			.replace(/\r\n/g, '')
			.replace(/\n/g, '');
	},
	downloadFile: (data, type, filename) => {
		const element = document.createElement('a');
		const file = new Blob([data], {
			type: type
		});
		element.href = URL.createObjectURL(file);
		element.download = filename;
		document.body.appendChild(element);
		element.click();
	},
	downloadSrt: async(translationId, filename) => {
		let translation = await TranslationService.getById(translationId);
		TranslationService.downloadFile(SRTService.export(translation.lines), 'application/x-subrip', filename + '.srt');
	}
};

export default TranslationService;
